import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/use-auth';
import { Input, Label, Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui';
import { MerchantSelect } from '@/components/features/merchants/merchant-select';
import { DateRangePicker, type DateRange } from '@/components/shared/date-range-picker';
import type { ListAdminSubscriptionsParams, SubscriptionStatus } from '@/types/admin-subscription';

const ALL_STATUSES: SubscriptionStatus[] = ['active', 'pending_termination', 'terminated', 'cancelled'];

interface SubscriptionFiltersProps {
  /** Server-seitige Filter (resetten Cursor) */
  filters: ListAdminSubscriptionsParams;
  onFiltersChange: (filters: ListAdminSubscriptionsParams) => void;
  /** Client-seitige Filter (kein Cursor-Reset) */
  operatorId?: string;
  onOperatorChange: (value: string) => void;
  amountMin?: string;
  amountMax?: string;
  onAmountMinChange: (value: string) => void;
  onAmountMaxChange: (value: string) => void;
  dateFrom?: Date;
  dateTo?: Date;
  onDateRangeChange: (range: DateRange) => void;
}

export function SubscriptionFilters({
  filters,
  onFiltersChange,
  operatorId,
  onOperatorChange,
  amountMin,
  amountMax,
  onAmountMinChange,
  onAmountMaxChange,
  dateFrom,
  dateTo,
  onDateRangeChange,
}: SubscriptionFiltersProps) {
  const { t } = useTranslation('subscriptions');
  const { user } = useAuth();
  const isPlatformAdmin = user?.role === 'platform_admin';

  const handleStatusChange = (value: string) => {
    onFiltersChange({
      ...filters,
      status: value === 'all' ? undefined : (value as SubscriptionStatus),
      cursor: undefined,
    });
  };

  const handleMerchantChange = (value: string | undefined) => {
    onFiltersChange({
      ...filters,
      merchantId: value,
      cursor: undefined,
    });
  };

  const handleServiceChange = (value: string) => {
    onFiltersChange({
      ...filters,
      serviceId: value || undefined,
      cursor: undefined,
    });
  };

  return (
    <div className="mb-4 space-y-3">
      {/* Zeile 1: bestehende Server-Filter */}
      <div className="flex flex-wrap items-center gap-3">
        <Select value={filters.status ?? 'all'} onValueChange={handleStatusChange}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder={t('list.filters.status')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('list.filters.allStatuses')}</SelectItem>
            {ALL_STATUSES.map((s) => (
              <SelectItem key={s} value={s}>
                {t(`statuses.${s}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {isPlatformAdmin && (
          <MerchantSelect
            value={filters.merchantId}
            onChange={handleMerchantChange}
            className="w-[220px]"
          />
        )}

        <Input
          placeholder={t('list.filters.service')}
          value={filters.serviceId ?? ''}
          onChange={(e) => handleServiceChange(e.target.value)}
          className="w-[180px]"
        />
      </div>

      {/* Zeile 2: Client-seitige Filter (kein Cursor-Reset) */}
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <Label className="text-xs text-muted-foreground">{t('list.filters.operator', 'Operator')}</Label>
          <Input
            placeholder={t('list.filters.operatorPlaceholder', 'z.B. D1, O2')}
            value={operatorId ?? ''}
            onChange={(e) => onOperatorChange(e.target.value)}
            className="w-[130px]"
          />
        </div>

        <div className="flex items-end gap-1.5">
          <div>
            <Label className="text-xs text-muted-foreground">{t('list.filters.amountMin', 'Min EUR')}</Label>
            <Input
              type="number"
              step="0.01"
              min="0"
              placeholder="0.00"
              value={amountMin ?? ''}
              onChange={(e) => onAmountMinChange(e.target.value)}
              className="w-[100px]"
            />
          </div>
          <span className="pb-2 text-muted-foreground">–</span>
          <div>
            <Label className="text-xs text-muted-foreground">{t('list.filters.amountMax', 'Max EUR')}</Label>
            <Input
              type="number"
              step="0.01"
              min="0"
              placeholder="999.99"
              value={amountMax ?? ''}
              onChange={(e) => onAmountMaxChange(e.target.value)}
              className="w-[100px]"
            />
          </div>
        </div>

        <DateRangePicker
          from={dateFrom}
          to={dateTo}
          onChange={onDateRangeChange}
        />
      </div>
    </div>
  );
}
